"use client";

import { useEffect, useState } from "react";
import { Polygon, Polyline, CircleMarker, Marker } from "react-leaflet";

import { Coordinates, PolygonArea } from "../../types/MapData";

import { LatLng, DivIcon } from "leaflet";
import { getPolygonLabelPosition, createPolygonLabelIcon } from "../../MapUtil";

type Props = {
  polygons: PolygonArea[];
  showPolygons?: boolean;
  showLabels?: boolean;
  selectedPolygonIds?: string[];
  isEditing?: boolean; // 指定しない場合ドットの表示は無し
  onCoordChange?: (polygonId: string, coordIndex: number, newCoord: Coordinates) => void;
};

export const PolygonDrawer = (props: Props) => {
  const {
    isEditing,
    onCoordChange,
    selectedPolygonIds = [],
    showPolygons = true,
    showLabels = true,
  } = props;
  const [polygons, setPolygons] = useState<PolygonArea[]>([]);

  const toLatLng = (val: Coordinates) => {
    return new LatLng(val.y, val.x, val.z);
  };

  // 編集時のドットマーク
  const createDotIcon = (color: string) => {
    return new DivIcon({
      className: "custom-dot-icon",
      html: `<div style="width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; border: 1px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.5);"></div>`,
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    });
  };

  useEffect(() => {
    setPolygons([...props.polygons]);
  }, [props.polygons]);

  return (
    <>
      {polygons.map((polygon) => {
        if (polygon.coords.length === 0) return undefined;
        const isSelected = selectedPolygonIds.includes(polygon.id);
        const positions = polygon.coords.map((coord) => toLatLng(coord));
        const labelPosition =
          positions.length >= 3 && polygon.label?.trim()
            ? getPolygonLabelPosition(polygon.coords)
            : undefined;

        return (
          <div key={polygon.id}>
            {showPolygons && polygon.completed && positions.length >= 3 && (
              <Polygon
                positions={polygon.coords.map((coord) => [coord.y, coord.x])}
                pathOptions={{
                  color: polygon.color,
                  opacity: 1,
                  fillColor: polygon.color,
                  fillOpacity: isSelected ? 0.8 : 0.2,
                }}
              />
            )}
            {showLabels && labelPosition && polygon.label && (
              <Marker
                position={new LatLng(labelPosition.y, labelPosition.x)}
                icon={createPolygonLabelIcon(polygon.label, polygon.color, isSelected)}
                interactive={false}
                zIndexOffset={isSelected ? 1000 : 0}
              />
            )}
            {showPolygons && !polygon.completed && positions.length >= 2 && (
              <Polyline
                positions={positions}
                pathOptions={{ color: polygon.color, weight: isSelected ? 5 : 2 }}
              />
            )}

            {isEditing !== undefined &&
              positions.map((latlng, index) => {
                if (isEditing && polygon.completed) {
                  return (
                    <Marker
                      key={`${polygon.id}-${index}`}
                      position={latlng}
                      draggable={true}
                      icon={createDotIcon(polygon.color)}
                      eventHandlers={{
                        dragend: (e) => {
                          const marker = e.target;
                          const position = marker.getLatLng();
                          const newCoord: Coordinates = { x: position.lng, y: position.lat };
                          onCoordChange?.(polygon.id, index, newCoord);
                        },
                      }}
                    />
                  );
                } else {
                  return (
                    <CircleMarker
                      key={`${polygon.id}-${index}`}
                      center={latlng}
                      radius={3}
                      pathOptions={{
                        color: polygon.color,
                        fillColor: polygon.color,
                        fillOpacity: 1,
                        weight: 2,
                      }}
                    />
                  );
                }
              })}
          </div>
        );
      })}
    </>
  );
};
